import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concat, from, of } from 'rxjs';
import { catchError, exhaustMap, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';

import { stripChildrenOutlets } from '@core/common-legacy';
import { createEffectFactory } from '@core/ngrx-legacy';
import { injectReportsDashboardHttpClient } from '@core/reports-contracts';
import { encodeSlugWithId } from '@core/reports-util';
import { generateUuid } from '@core/utils-legacy';

import { ReportsDashboardsHttpClient } from './dashboards.http-client';
import { ReportsDashboardsActions } from './dashboards.store';
import { ReportsComponentsActions } from '../components/components.store';

@Injectable()
export class ReportsDashboardsEffects {
  private readonly actions$ = inject(Actions);
  private readonly http =
    injectReportsDashboardHttpClient({ optional: true }) ?? inject(ReportsDashboardsHttpClient);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  public effects = createEffectFactory(ReportsDashboardsActions, this.actions$);

  public fetchCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsDashboardsActions.fetchCollection),
      switchMap(() =>
        this.http.fetchCollection().pipe(
          mergeMap(({ dashboards }) => [
            ReportsDashboardsActions.fetchCollectionSuccess({ payload: { data: { dashboards } } }),
            ReportsComponentsActions.fetchCollectionSuccess({
              payload: {
                data: dashboards.map(dashboard => dashboard.components).flat(),
              },
            }),
          ]),
          catchError(error =>
            of(ReportsDashboardsActions.fetchCollectionFailed({ payload: { error } })),
          ),
        ),
      ),
    ),
  );

  public updateNameAndSymbol$ = this.effects(
    ReportsDashboardsActions.updateNameAndSymbol,
    ({ payload }) =>
      this.http.updateOne(payload.data).pipe(
        map(data =>
          ReportsDashboardsActions.updateNameAndSymbolSuccess({
            payload: { data: data.dashboard },
          }),
        ),
        catchError(error =>
          of(
            ReportsDashboardsActions.updateNameAndSymbolFailed({
              payload: { error, data: { id: payload.data.id } },
            }),
          ),
        ),
      ),
  );

  public updateNameAndSymbolSuccess$ = this.effects(
    ReportsDashboardsActions.updateNameAndSymbolSuccess,
    ({ payload }) =>
      this.router.navigate(['analytics/boards', payload.data.id], { replaceUrl: true }),
    {
      dispatch: false,
    },
  );
  // TODO: updateNameFailed$

  public updateVisibility$ = this.effects(
    ReportsDashboardsActions.updateVisibility,
    ({ payload }) =>
      this.http.updateOne(payload.data).pipe(
        map(data =>
          ReportsDashboardsActions.updateVisibilitySuccess({ payload: { data: data.dashboard } }),
        ),
        catchError(error =>
          of(
            ReportsDashboardsActions.updateVisibilityFailed({
              payload: { error, data: { id: payload.data.id } },
            }),
          ),
        ),
      ),
  );

  public updateFilters$ = this.effects(
    ReportsDashboardsActions.updateFiltersOptimistic,
    ({ payload }) =>
      this.http.updateOne(payload.data).pipe(
        map(data =>
          ReportsDashboardsActions.updateFiltersOptimisticSuccess({
            payload: { data: data.dashboard, correlationId: payload.correlationId },
          }),
        ),
        catchError(error =>
          of(
            ReportsDashboardsActions.updateFiltersOptimisticFailed({
              payload: {
                error,
                data: { id: payload.data.id },
                correlationId: payload.correlationId,
              },
            }),
          ),
        ),
      ),
  );

  public createOne$ = this.effects(
    ReportsDashboardsActions.createOneOptimistic,
    ({ payload }) =>
      this.http.createOne(payload.data).pipe(map(data => ({ data: data.dashboard }))),
    {
      customMapOperator: exhaustMap,
    },
  );

  public afterCreate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ReportsDashboardsActions.createOneOptimisticSuccess,
          ReportsDashboardsActions.duplicateOneSuccess,
        ),
        tap(({ payload }) => {
          const urlTree = stripChildrenOutlets(
            ['dialog'],
            this.router.createUrlTree(this.route.snapshot.url),
          );

          this.router.navigateByUrl(urlTree).then(() => {
            this.router.navigate([
              'analytics/boards',
              encodeSlugWithId(payload.data.name, payload.data.id),
            ]);
          });
        }),
      ),
    { dispatch: false },
  );

  public removeOne$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsDashboardsActions.removeOneOptimistic),
      map(({ payload }) => payload.dashboard),
      mergeMap(dashboard =>
        this.http.removeOne(dashboard.id).pipe(
          map(() =>
            ReportsDashboardsActions.removeOneOptimisticSuccess({ payload: { data: dashboard } }),
          ),
          catchError(error =>
            of(
              ReportsDashboardsActions.removeOneOptimisticFailed({
                payload: { error, data: dashboard.id },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public removeOneSuccess$ = this.effects(
    ReportsDashboardsActions.removeOneOptimisticSuccess,
    () => {
      from(this.router.navigate(['/analytics/redirect']))
        .pipe(take(1))
        .subscribe();
    },
    { dispatch: false },
  );

  public duplicateOne$ = this.effects(
    ReportsDashboardsActions.duplicateOne,
    ({ payload }) =>
      this.http.createOne(payload.data).pipe(
        mergeMap(({ dashboard }) => [
          ReportsDashboardsActions.duplicateOneSuccess({
            payload: {
              data: dashboard,
              correlationId: payload.correlationId,
            },
          }),
          ReportsComponentsActions.addMany({
            payload: {
              data: dashboard.components,
            },
          }),
        ]),
        catchError(error =>
          of(
            ReportsDashboardsActions.duplicateOneFailed({
              payload: {
                error,
                data: undefined,
                correlationId: payload.correlationId,
              },
            }),
          ),
        ),
      ),
    {
      customMapOperator: exhaustMap,
    },
  );

  public updatePositions$ = this.effects(
    ReportsDashboardsActions.updatePositionsOptimistic,
    ({ payload }) =>
      this.http.updatePositions(payload.data).pipe(
        map(data =>
          ReportsDashboardsActions.updatePositionsOptimisticSuccess({
            payload: { data: data.dashboards, correlationId: payload.correlationId },
          }),
        ),
        catchError(error =>
          of(
            ReportsDashboardsActions.updatePositionsOptimisticFailed({
              payload: {
                error,
              },
            }),
          ),
        ),
      ),
  );

  public moveComponent$ = this.effects(ReportsDashboardsActions.moveComponent, ({ payload }) =>
    of(payload.localId ?? generateUuid()).pipe(
      switchMap(correlationId =>
        // first, update grid props of component
        concat(
          of(
            ReportsComponentsActions.updateGridPropsOptimistic({
              payload: {
                correlationId,
                data: {
                  dashboardId: payload.targetDashboardId,
                  components: [
                    {
                      grid: payload.grid,
                      id: payload.componentId,
                    },
                  ],
                },
              },
            }),
          ),
          // then make an http request to update grid position and parent dashboard
          this.http
            .moveComponent(
              payload.componentId,
              payload.sourceDashboardId,
              payload.targetDashboardId,
              payload.grid,
            )
            .pipe(
              // in the end update both component grid props and target dashboard
              switchMap(() => [
                ReportsDashboardsActions.moveComponentSuccess(),
                ReportsComponentsActions.updateGridPropsOptimisticSuccess({
                  payload: { correlationId },
                }),
              ]),
              catchError(error => [
                // or rollback both
                ReportsDashboardsActions.moveComponentFailed({
                  payload: {
                    error,
                    componentId: payload.componentId,
                    sourceDashboardId: payload.sourceDashboardId,
                    targetDashboardId: payload.targetDashboardId,
                  },
                }),
                ReportsComponentsActions.updateGridPropsOptimisticFailed({
                  payload: { error, correlationId },
                }),
              ]),
            ),
        ),
      ),
    ),
  );
}
